/* The ledger's screens (piece 23): the Chart of Accounts list, an account's record, the Manual Journal, the periods and the blocked queue. */
.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.hint {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/**
 * 🔨 The warning a close carries inside its own dialog (decision 0456). It is
 * not an Alert Banner: a banner that states a problem has to offer an action
 * (piece 3), and in a dialog the action is the dialog's own footer.
 */
/* 🔒 Decision 2182: a refusal read inside a window, above its Footer, where the page's banner cannot be seen. */
.problem {
  margin: 0;
  color: var(--status-error-text);
  font-size: var(--font-size-small);
}

.warningNote {
  margin: 0;
  padding: var(--space-2);
  border-radius: var(--radius-medium);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
}

.row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-3);
}

.row > * {
  flex: 1 1 200px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}






.amount {
  font-variant-numeric: var(--figures);
  text-align: right;
  min-width: 7rem;
}

/* A posted entry's lines, each carrying its own debit or credit column rather
   than one amount whose direction the reader works out. The row wraps onto its
   own line beneath the entry's heading, which .item's wrap already allows.
   🔨 Three aligned columns on a grid, never a `table`: every real table in the
   application goes through List Viewer (piece 4), and this is a fixed readout
   of one entry rather than a sortable, pageable list. */
/* 🔒 Account, the line's own note, then the two amounts (decision 0466). The note
   takes the slack because it is prose and the account is a fixed label. */
.entryLines {
  flex: 1 1 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: var(--space-1) var(--space-3);
  font-size: var(--font-size-small);
}

/* A note long enough to crowd the amounts wraps rather than pushing them off the row. */
.entryLineNote {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--text-muted);
}

/* 🔨 The memo stands under the number and the date as its own sentence, condensed
   out of the heading the three of them used to run together in (decision 0466). */
.entryMemo {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
}

.entryLineHead {
  color: var(--text-muted);
}

.entryTotal {
  font-weight: var(--font-weight-bold);
  border-top: 1px solid var(--tile-border);
  padding-block-start: var(--space-1);
}

.crumbs {
  margin: 0;
  font-size: var(--font-size-small);
}

.crumbs a {
  color: var(--navy-primary);
}

/* T3733: a journal line is a cell of a line table (decision 0647), so each of its fields takes the whole column and the Account is read whole. */
.journalLine > [data-field-size] {
  --field-box-max: none;
}

/* The journal's line grid: account, debit, credit, company and a description, one line per row, stacked on a narrow screen. */
.journalLine {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-2);
  align-items: end;
  padding-block: var(--space-2);
  border-top: 1px solid var(--tile-border);
}

@media (--bp-md) {
  .journalLine {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1.5fr) auto;
  }
}

.balance {
  font-weight: var(--font-weight-bold);
}

/* T3713: the reason Post is greyed, on a line of its own under the footer's buttons. */
.postReason {
  flex-basis: 100%;
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

.balanceOff {
  color: var(--status-error-text);
}

.balanceOn {
  color: var(--status-good-text);
}

.fieldLabel {
  display: block;
  font-size: var(--font-size-small);
  color: var(--text-muted);
  margin-block-end: var(--space-1);
}

/* Decision 0430: an account's page opens read-only, so its fields are values
   before they are controls. The same wrap as .row, without the control heights. */
.facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin: 0;
}

.facts > * {
  flex: 1 1 200px;
}

.select {
  width: 100%;
  min-height: var(--field-height);
  padding-inline: var(--space-2);
  border: var(--field-border-width) solid var(--border);
  border-radius: var(--radius-small);
  background: var(--surface);
  color: var(--text);
  font: inherit;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-small);
}

.table th,
.table td {
  padding: var(--space-1) var(--space-2);
  border-bottom: 1px solid var(--tile-border);
  text-align: left;
  vertical-align: top;
}

.table td.amount,
.table th.amount {
  text-align: right;
  font-variant-numeric: var(--figures);
}

.tableWrap {
  overflow-x: auto;
}

/* Piece 23, decision 0407: one entry's working, behind the super-admin control
   decision 0375 settled for the Deal floor. The figures are the answer; the
   arithmetic is how the answer was reached, and it unfolds when asked for. */

.working {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  align-items: flex-start;
  width: 100%;
}

.workingTrigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-caption);
  cursor: pointer;
}

.workingTrigger:hover {
  color: var(--navy-primary);
}

.workingTrigger svg {
  width: var(--icon-size-inline);
  height: var(--icon-size-inline);
}

.workingLines {
  width: 100%;
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-small);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  line-height: var(--line-height-base);
  list-style: none;
  overflow-x: auto;
}

.workingLine {
  white-space: nowrap;
}
